import { useEffect, useState, useRef } from 'react'
import { useAuth } from './useAuth'
import toast from 'react-hot-toast'
import Pusher from 'pusher-js'
import AuthenticatedEventSource from '../utils/AuthenticatedEventSource'

const REALTIME_EVENT_TYPES = [
  'new_material_request', 'material_request_status_update', 'new_movement', 'new_stock_alert',
  'material_request_updated', 'transfer_completed', 'material_request_completed',
  'new_purchase_order', 'purchase_order_status_update', 'purchase_order_items_received',
  'cron_sync_status', 'cron_sync_result', 'defect_sheet_updated'
]

export const useSSE = () => {
  const [isConnected, setIsConnected] = useState(false)
  const [isPusherConnected, setIsPusherConnected] = useState(false)
  const { user, token } = useAuth()
  const eventSourceRef = useRef(null)
  const audioCtxRef = useRef(null)
  const audioInitializedRef = useRef(false)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const isReconnectingRef = useRef(false)
  const isConnectedRef = useRef(false) // Track connection state with ref for event handlers
  const maxReconnectAttempts = 5 // Limit reconnection attempts
  const consecutive401ErrorsRef = useRef(0) // Track consecutive 401 errors

  const initializeAudio = async () => {
    if (audioInitializedRef.current && audioCtxRef.current) {
      return audioCtxRef.current
    }
    
    try {
      const AudioContextImpl = window.AudioContext || window.webkitAudioContext
      if (!AudioContextImpl) return null
      
      let ctx = audioCtxRef.current
      if (!ctx) {
        ctx = new AudioContextImpl()
        audioCtxRef.current = ctx
      }
      
      if (ctx.state === 'suspended') {
        await ctx.resume()
      }
      
      audioInitializedRef.current = true
      return ctx
    } catch (_) {
      return null
    }
  }

  const playNotificationSound = async () => {
    try {
      const ctx = await initializeAudio()
      if (!ctx) return
      
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()
      oscillator.type = 'sine'
      oscillator.frequency.value = 880
      const now = ctx.currentTime
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35)
      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start(now)
      oscillator.stop(now + 0.36)
    } catch (_) {
      // Silent fail
    }
  }

  // Initialize audio on first user interaction
  useEffect(() => {
    const initOnInteraction = async () => {
      if (!audioInitializedRef.current) {
        await initializeAudio()
      }
    }
    
    const events = ['click', 'keydown', 'touchstart', 'mousedown']
    events.forEach(event => {
      window.addEventListener(event, initOnInteraction, { once: true })
    })
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, initOnInteraction)
      })
    }
  }, [])

  // Shared handler for real-time events (SSE and Pusher)
  const handleRealtimeEvent = (data) => {
    if (!data || !data.type) return
    switch (data.type) {
      case 'connected':
      case 'heartbeat':
        break
      case 'new_material_request':
        toast.success(`New material request from ${data.data?.userName}`, { duration: 5000 })
        playNotificationSound()
        window.dispatchEvent(new CustomEvent('newMaterialRequest', { detail: data.data }))
        break
      case 'material_request_status_update': {
        let toastType = 'success', showToast = true, playSound = true
        switch (data.data?.status) {
          case 'read': showToast = false; toastType = 'info'; break
          case 'separating': toastType = 'info'; break
          case 'separated':
          case 'completed': toastType = 'success'; break
          case 'cancelled': toastType = 'error'; playSound = true; break
        }
        if (showToast) toast[toastType](data.data?.message, { duration: 5000 })
        if (playSound) playNotificationSound()
        window.dispatchEvent(new CustomEvent('materialRequestStatusUpdate', { detail: data.data }))
        break
      }
      case 'new_movement':
        window.dispatchEvent(new CustomEvent('newMovement', { detail: data.data }))
        break
      case 'new_stock_alert':
        toast.error(`Stock Alert: ${data.data?.item_name} at ${data.data?.location_name} is ${data.data?.alertType === 'critical' ? 'CRITICAL' : 'low stock'}!`, { duration: 7000 })
        playNotificationSound()
        window.dispatchEvent(new CustomEvent('newStockAlert', { detail: data.data }))
        break
      case 'material_request_updated':
        window.dispatchEvent(new CustomEvent('materialRequestUpdated', { detail: data.data }))
        playNotificationSound()
        break
      case 'transfer_completed':
        window.dispatchEvent(new CustomEvent('transferCompleted', { detail: data.data }))
        playNotificationSound()
        break
      case 'material_request_completed':
        window.dispatchEvent(new CustomEvent('materialRequestCompleted', { detail: data.data }))
        playNotificationSound()
        break
      case 'new_purchase_order':
        toast.success(`New purchase order ${data.data?.poNumber} from ${data.data?.userName}`, { duration: 5000 })
        playNotificationSound()
        window.dispatchEvent(new CustomEvent('newPurchaseOrder', { detail: data.data }))
        break
      case 'purchase_order_status_update':
        toast.success(data.data?.message, { duration: 5000 })
        playNotificationSound()
        window.dispatchEvent(new CustomEvent('purchaseOrderStatusUpdate', { detail: data.data }))
        break
      case 'purchase_order_items_received':
        toast.success(data.data?.message, { duration: 5000 })
        playNotificationSound()
        window.dispatchEvent(new CustomEvent('purchaseOrderItemsReceived', { detail: data.data }))
        break
      case 'cron_sync_status':
        window.dispatchEvent(new CustomEvent('cronSyncStatusUpdate', { detail: data.data }))
        break
      case 'cron_sync_result':
        window.dispatchEvent(new CustomEvent('cronSyncResult', { detail: data.data }))
        break
      case 'defect_sheet_updated':
        if (user?.role_id === 1 && Number(data.data?.actor_id) !== Number(user.id)) {
          window.dispatchEvent(new CustomEvent('jobsNavNotification'))
        }
        window.dispatchEvent(new CustomEvent('defectSheetUpdated', { detail: data.data }))
        break
      default:
        break
    }
  }

  const connectSSE = async () => {
    if (!user || !token || isReconnectingRef.current) {
      return
    }

    // Stop trying if we've exceeded max attempts
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      setIsConnected(false)
      isConnectedRef.current = false
      return
    }

    // Stop if we've had too many 401 errors (token issues)
    if (consecutive401ErrorsRef.current >= 3) {
      setIsConnected(false)
      isConnectedRef.current = false
      return
    }
    
    try {
      // Get fresh token from storage (in case it was refreshed)
      const currentToken = localStorage.getItem('token')
      if (!currentToken) {
        setIsConnected(false)
        isConnectedRef.current = false
        return
      }

      // Close existing connection if any
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }

      // Auto-detect API URL: use api.suite.nz subdomain in production, otherwise use env var or localhost
      const getApiBaseUrl = () => {
        // Check if running on suite.nz domain (production) - with or without www
        if (typeof window !== 'undefined') {
          const hostname = window.location.hostname
          if (hostname === 'suite.nz' || hostname === 'www.suite.nz') {
            return 'https://api.suite.nz'
          }
        }
        return import.meta.env.VITE_API_URL || 'http://localhost:3001'
      }

      const apiBaseUrl = getApiBaseUrl()
      const sseUrl = apiBaseUrl.endsWith('/api') 
        ? `${apiBaseUrl}/sse/events`
        : `${apiBaseUrl}/api/sse/events`;
      
      const eventSource = new AuthenticatedEventSource(sseUrl, { token: currentToken })

      eventSourceRef.current = eventSource

      // Connection event handlers
      eventSource.onopen = () => {
        setIsConnected(true)
        isConnectedRef.current = true
        reconnectAttemptsRef.current = 0
        consecutive401ErrorsRef.current = 0 // Reset 401 counter on successful connection
        isReconnectingRef.current = false
      }

      eventSource.onerror = (error) => {
        const currentState = eventSource.readyState
        
        // Check if error is 401 (Unauthorized) - token expired
        // EventSource doesn't give us status code directly, but we can infer from connection closing immediately
        if (currentState === AuthenticatedEventSource.CLOSED) {
          setIsConnected(false)
          isConnectedRef.current = false
          
          // If connection closed immediately, it might be a 401
          // We'll track this and stop after a few attempts
          consecutive401ErrorsRef.current++
          
          // Stop if too many 401s (likely token issue)
          if (consecutive401ErrorsRef.current >= 3) {
            return
          }
          
          // Stop if we've exceeded max attempts
          if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
            return
          }
          
          // Attempt to reconnect if connection was lost
          // Clear any existing reconnect timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
          }
          
          // Exponential backoff: 2s, 4s, 8s, 16s, max 30s
          const delay = Math.min(2000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
          reconnectAttemptsRef.current++
          
          isReconnectingRef.current = true
          reconnectTimeoutRef.current = setTimeout(() => {
            isReconnectingRef.current = false
            connectSSE()
          }, delay)
        } else if (currentState === AuthenticatedEventSource.CONNECTING) {
          // Connection is trying to reconnect, keep status as disconnected for now
        setIsConnected(false)
          isConnectedRef.current = false
        }
        // If OPEN, don't change status (connection is fine)
      }

      // Message event handler  
      eventSource.onmessage = (event) => {
        try {
          if (event.data === '[object Object]' || !event.data || event.data.trim() === '') return
          const data = JSON.parse(event.data)
          handleRealtimeEvent(data)
        } catch (error) {
          // Silent
        }
      }

    } catch (error) {
      // Silent error handling
      setIsConnected(false)
    }
  }

  // Pusher: real-time that works on Vercel serverless (no in-memory SSE dependency)
  useEffect(() => {
    const pusherKey = import.meta.env.VITE_PUSHER_APP
    const pusherCluster = import.meta.env.VITE_PUSHER_CLUSTER || 'us2'
    if (!pusherKey || !user?.id || !token) return

    const getApiBaseUrl = () => {
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname
        if (hostname === 'suite.nz' || hostname === 'www.suite.nz') return 'https://api.suite.nz'
      }
      return import.meta.env.VITE_API_URL || 'http://localhost:3001'
    }
    const apiBaseUrl = getApiBaseUrl().replace(/\/$/, '')
    const authEndpoint = `${apiBaseUrl}/api/pusher/auth`
    const currentToken = localStorage.getItem('token')

    const pusher = new Pusher(pusherKey, {
      cluster: pusherCluster,
      channelAuthorization: {
        endpoint: authEndpoint,
        headers: { Authorization: `Bearer ${currentToken}` }
      }
    })
    pusher.connection.bind('connected', () => setIsPusherConnected(true))
    pusher.connection.bind('disconnected', () => setIsPusherConnected(false))

    const channels = ['private-app']
    if (user.role_id === 1) channels.push('private-admin')
    if (user.role_id === 2) channels.push('private-painter')
    channels.push(`private-user-${user.id}`)

    const unbind = []
    channels.forEach((channelName) => {
      const channel = pusher.subscribe(channelName)
      REALTIME_EVENT_TYPES.forEach((eventType) => {
        const handler = (payload) => handleRealtimeEvent(payload)
        channel.bind(eventType, handler)
        unbind.push(() => channel.unbind(eventType, handler))
      })
    })

    return () => {
      unbind.forEach((fn) => fn())
      channels.forEach((name) => pusher.unsubscribe(name))
      pusher.disconnect()
    }
  }, [user?.id, user?.role_id, token])

  useEffect(() => {
    if (!user || !token) return
    // When Pusher is configured, skip SSE to avoid Vercel 300s timeout on long-lived connections
    if (import.meta.env.VITE_PUSHER_APP) return
    connectSSE()

    // Reconnect when token is refreshed
    const handleTokenRefreshed = () => {
      if (import.meta.env.VITE_PUSHER_APP) return
      consecutive401ErrorsRef.current = 0
      reconnectAttemptsRef.current = 0
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      isReconnectingRef.current = false
      connectSSE()
    }

    // Reconnect on user interaction (when user comes back after inactivity)
    const handleUserActivity = () => {
      if (import.meta.env.VITE_PUSHER_APP) return
      if (!isConnectedRef.current && !isReconnectingRef.current) {
        // Reset counters on user activity (user might have refreshed token)
        if (consecutive401ErrorsRef.current >= 3) {
          consecutive401ErrorsRef.current = 0
        }
        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          reconnectAttemptsRef.current = 0
        }
        
        const currentState = eventSourceRef.current?.readyState
        // Reconnect if connection is closed or doesn't exist
        if (!eventSourceRef.current || currentState === AuthenticatedEventSource.CLOSED || currentState === undefined) {
          // Clear any pending reconnect timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
          }
          reconnectAttemptsRef.current = 0
          isReconnectingRef.current = false
          connectSSE()
        }
      }
    }

    // Listen for token refresh events
    window.addEventListener('tokenRefreshed', handleTokenRefreshed)

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true })
    })

      return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefreshed)
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity)
      })
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
        if (eventSourceRef.current) {
          eventSourceRef.current.close()
          eventSourceRef.current = null
        }
        setIsConnected(false)
      isConnectedRef.current = false
      isReconnectingRef.current = false
      reconnectAttemptsRef.current = 0
      consecutive401ErrorsRef.current = 0
    }
  }, [user, token])

  return { isConnected: isConnected || isPusherConnected }
}
